import React, { useState, useEffect, ChangeEvent, FormEvent, useCallback } from 'react';
import { Typography, Button, Divider, Grid, Box } from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import { useToast } from '../../../../context/ToastContext';
import useAxios from 'packages/novous_web/src/api/useAxios';
import apiClient from 'packages/novous_web/src/api/apiClient';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { getAllCountries, getStatesOfCountry, CountryType, StateType } from '../../../../utils/locationUtils';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import InputField from 'packages/novous_web/src/common/components/InputField';
import DateField from 'packages/novous_web/src/common/components/DateField';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import file from 'packages/novous_web/src/assets/icons/Image File.svg';
import ArrowBackIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';

type CompanyOption = {
  value: string;
  label: string;
};

type FormData = {
  company: string;
  timeZones: string;
  userRole: string;
  userName: string;
  password: '' | string;
  firstName: string;
  lastName: string;
  dob: string;
  experience: string;
  employerId: string;
  jobType: string;
  dateOfJoining: string;
  licenseNo: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  country: string; // Will store full country name (e.g., 'India', 'United States')
  fullAddress: string;
  selectedFile: File | null;
  selectVehicle: string;
  selectDate: string;
  selectTime: string;
  previewUrl: string;
  selectGroup: string;
};

type Errors = Partial<Record<keyof FormData, string>>;

// Main Component
const CreateUser: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    company: '',
    timeZones: '',
    userRole: '',
    userName: '',
    password: '',
    firstName: '',
    lastName: '',
    dob: '',
    experience: '',
    employerId: '',
    jobType: '',
    dateOfJoining: '',
    licenseNo: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '', // Stored as full country name
    fullAddress: '',
    selectedFile: null,
    selectVehicle: '',
    selectDate: '',
    selectTime: '',
    previewUrl: '',
    selectGroup: '',
  });

  const [errors, setErrors] = useState<Errors>({});
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [states, setStates] = useState<StateType[]>([]);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [userNameExists, setUserNameExists] = useState<boolean>(false);
  const [checkingUserName, setCheckingUserName] = useState<boolean>(false);
  const [userNameCheckTimeout, setUserNameCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasShownError, setHasShownError] = useState<boolean>(false);
  const { showToast } = useToast();

  // Auth: derive role options based on authorities
  const { user } = useAuth();
  const authorities = user?.authorities;
  const id = user?.id;

  const isSuperAdmin = Array.isArray(authorities)
    ? authorities.includes('ROLE_SUPER_ADMIN')
    : typeof authorities === 'string'
      ? authorities
        .split(',')
        .map((s) => s.trim())
        .includes('ROLE_SUPER_ADMIN')
      : false;

  const isAdmin = Array.isArray(authorities)
    ? authorities.includes('ROLE_ADMIN')
    : typeof authorities === 'string'
      ? authorities
        .split(',')
        .map((s) => s.trim())
        .includes('ROLE_ADMIN')
      : false;

  const isFleetManager = Array.isArray(authorities)
    ? authorities.includes('ROLE_FLEET_MANAGER')
    : typeof authorities === 'string'
      ? authorities
        .split(',')
        .map((s) => s.trim())
        .includes('ROLE_FLEET_MANAGER')
      : false;

  type RoleOption = { value: string; label: string };

  // Role options: if super admin, allow Admin, else prepopulate Fleet Manager
  const roleOptions: RoleOption[] = isSuperAdmin
    ? [{ value: 'ROLE_ADMIN', label: 'Admin' }]
    : isAdmin
      ? [{ value: 'ROLE_FLEET_MANAGER', label: 'Fleet Manager' }]
      : [{ value: 'ROLE_LOCATION_MANAGER', label: 'Location Manager' }];

  // API Hook for fetching Company Names
  const {
    data: CompanyData,
    loading: CompanyLoading,
    fetchData: fetchCompanyData,
  } = useAxios<any>(API_ENDPOINTS.users.COMPANY_NAMES, 'GET');

  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id} `, 'GET');

  const {
    data: timeZonesData,
    error: timeZonesError,
    loading: timeZonesLoading,
    fetchData: fetchtimeZonesData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.utility.GET_TIMEZONE}/${formData.country} `, 'GET');

  useEffect(() => {
    if (user?.customer_id) {
      fetchGroupsData();
    }
  }, [user?.customer_id]);

  useEffect(() => {
    if (formData.country) {
      fetchtimeZonesData();
    }
  }, [formData.country]);

  let groupOptions: CompanyOption[] = [];

  if (groupsData && groupsData?.data) {
    groupOptions = groupsData?.data.map((item: string) => {
      return { value: item, label: item };
    });
  }

  useEffect(() => {
    fetchCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!CompanyData) return;

    try {
      // Backend sends JSON string in CompanyData.data
      let raw = CompanyData.data;
      if (typeof raw === 'string') raw = JSON.parse(raw);

      if (Array.isArray(raw)) {
        const opts = raw
          .filter((n: unknown) => typeof n === 'string' && n.trim().length > 0)
          .map((name: string) => ({ value: name, label: name }));
        setCompanyOptions(opts);
      } else {
        console.warn('Unexpected company data shape:', raw);
        setCompanyOptions([]);
      }
    } catch (err) {
      console.error('Failed to parse company list:', err);
      setCompanyOptions([]);
    }
  }, [CompanyData]);

  type UserAddResponse = {
    message?: string;
    status?: number; // backend may embed status in body
    statusCode?: number; // add statusCode property
    data?: any; // Add data property to handle nested response structures
  };

  // your existing hook, but tell TS what the POST returns
  const {
    data: UserAddData,
    loading: UserAddLoading,
    fetchData: UserAddRequestData,
    errorStatus: UserAddErrorStatus,
    error: UserAddError, // Add this to access the error message
  } = useAxios<UserAddResponse>(API_ENDPOINTS.users.CREATE_USERS, 'POST');

  // Effect 1: Load Countries on mount
  useEffect(() => {
    const loadedCountries = getAllCountries();
    setCountries(loadedCountries);
  }, []);

  // Effect 2: Load States when country changes
  useEffect(() => {
    if (formData.country) {
      // Find the country code from the country name
      const selectedCountry = countries.find((c) => c.label === formData.country);
      if (selectedCountry) {
        const loadedStates = getStatesOfCountry(selectedCountry.value);
        setStates(loadedStates);
        setFormData((prev) => ({ ...prev, state: '' }));
      }
    } else {
      setStates([]);
    }
  }, [formData.country, countries]);

  // Ensure that if current user is an admin, we don't keep a company value
  useEffect(() => {
    if (isAdmin && formData.company) {
      setFormData((prev) => ({ ...prev, company: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Effect 3: Auto-select role
  useEffect(() => {
    if (isSuperAdmin) {
      if (formData.userRole !== 'ROLE_ADMIN') {
        setFormData((prev) => ({ ...prev, userRole: 'ROLE_ADMIN' }));
      }
    } else {
      // prepopulate Fleet Manager for non-super-admins (as requested)
      if (!formData.userRole) {
        setFormData((prev) => ({ ...prev, userRole: 'ROLE_FLEET_MANAGER' }));
      }
    }
    if (isFleetManager) {
      if (!formData.userRole) {
        setFormData((prev) => ({ ...prev, userRole: 'ROLE_LOCATION_MANAGER' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Debounced user name existence check
  const checkUserNameExistence = useCallback(async (userName: string) => {
    if (!userName || userName.trim().length < 2) {
      setUserNameExists(false);
      setCheckingUserName(false);
      return;
    }

    setCheckingUserName(true);

    try {
      // Build the endpoint URL by appending user name to the base endpoint
      const endpoint = `${API_ENDPOINTS.users.USER_EXISTENCE}${encodeURIComponent(userName.trim())}`;

      const response = await apiClient({
        url: endpoint,
        method: 'GET',
        responseType: 'json',
      });

      const result = response.data;

      // Check if user exists - handle different response formats
      const userExists = result.exists === true || result.exists === 'true' || result.data === 'exists';

      setUserNameExists(userExists);
      setCheckingUserName(false);

      if (userExists) {
        setErrors((prev) => ({
          ...prev,
          userName: 'User name already exists. Please choose a different name.',
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.userName;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error checking user name existence:', error);
      setUserNameExists(false);
      setCheckingUserName(false);
    }
  }, []);

  // Effect to handle user name existence check with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (userNameCheckTimeout) {
      clearTimeout(userNameCheckTimeout);
    }

    // Set new timeout for debouncing
    if (formData.userName && formData.userName.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        checkUserNameExistence(formData.userName);
      }, 500); // 500ms debounce

      setUserNameCheckTimeout(timeoutId);
    } else {
      setUserNameExists(false);
      setCheckingUserName(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (userNameCheckTimeout) {
        clearTimeout(userNameCheckTimeout);
      }
    };
  }, [formData.userName, checkUserNameExistence]);

  // Cleanup effect on unmount
  useEffect(() => {
    return () => {
      if (userNameCheckTimeout) {
        clearTimeout(userNameCheckTimeout);
      }
    };
  }, [userNameCheckTimeout]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // Remove spaces from userName and password fields
    const sanitizedValue = name === 'userName' || name === 'password' ? value.replace(/\s/g, '') : value;

    // Apply sanitization for text inputs and textareas (excluding specific types)
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));

    // Clear error for the field on change
    setErrors((prev) => {
      if (!prev[name as keyof FormData]) return prev;
      const copy = { ...prev };
      delete copy[name as keyof FormData];
      return copy;
    });
  };

  const handleDropdownChange = (name: string) => (selectedValue: string) => {
    // For country field, store the full country name instead of code
    let valueToStore = selectedValue;
    if (name === 'country') {
      const selectedCountry = countries.find((c) => c.value === selectedValue);
      valueToStore = selectedCountry?.label || selectedValue;
    }

    setFormData((prev) => ({ ...prev, [name]: valueToStore }));

    // Clear error for the field on change
    setErrors((prev) => {
      if (!prev[name as keyof FormData]) return prev;
      const copy = { ...prev };
      delete copy[name as keyof FormData];
      return copy;
    });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      // File type validation
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }

      // File size validation (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        selectedFile: file,
        previewUrl: previewUrl,
      }));
    }
  };

  const handleRemoveImage = () => {
    if (formData.previewUrl) {
      URL.revokeObjectURL(formData.previewUrl);
    }
    setFormData((prev) => ({
      ...prev,
      selectedFile: null,
      previewUrl: '',
    }));
  };

  useEffect(() => {
    return () => {
      if (formData.previewUrl) {
        URL.revokeObjectURL(formData.previewUrl);
      }
    };
  }, [formData.previewUrl]);

  // Centralized Validation Function
  const validateForm = (data: FormData): Errors => {
    const newErrors: Errors = {};
    if (!isAdmin && !isFleetManager && !data.company) newErrors.company = 'Company is required'; // company required only when not admin and not fleet manager
    if (!data.timeZones) newErrors.timeZones = 'Time Zone is required';
    if (!data.userRole) newErrors.userRole = 'User Role is required';
    if (!data.userName) newErrors.userName = 'User Name is required';
    if (userNameExists) newErrors.userName = 'User name already exists. Please choose a different name.';
    if (checkingUserName) newErrors.userName = 'Checking user name availability...';
    if (!data.password) newErrors.password = 'Password is required';
    if (!data.firstName) newErrors.firstName = 'First Name is required';
    if (!data.country) newErrors.country = 'Country is required';
    if (!data.state) newErrors.state = 'State is required';
    if (!data.city) newErrors.city = 'City is required';
    if (data.userRole === 'ROLE_LOCATION_MANAGER' && !data.selectGroup)
      newErrors.selectGroup = 'Group is required'; // selectGroup required only for ROLE_LOCATION_MANAGER

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.email && !emailRegex.test(data.email)) {
      newErrors.email = 'Invalid email format';
    }
    return newErrors;
  };

  useEffect(() => {
    if (!isSuperAdmin) return;

    if (!formData.userName) return;

    // Don't append again if already added
    if (formData.userName.endsWith('Admin')) return;

    const timeout = setTimeout(() => {
      setFormData((prev) => {
        // Double check inside setter (important!)
        if (!prev.userName.endsWith('Admin')) {
          return {
            ...prev,
            userName: `${prev.userName}Admin`,
          };
        }
        return prev;
      });
    }, 1500);

    return () => clearTimeout(timeout);
  }, [formData.userName, isSuperAdmin]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors = validateForm(formData);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('Please correct the validation errors.', 'error');
      return;
    }

    // Additional check for user name availability
    if (userNameExists) {
      showToast('User name already exists. Please choose a different name.', 'error');
      return;
    }

    if (checkingUserName) {
      showToast('Please wait while we check user name availability', 'warning');
      return;
    }

    setErrors({});

    // Reset error state for new submission
    setHasShownError(false);

    // --- Build multipart FormData payload matching the expected format ---
    const fd = new FormData();

    // User image file (if selected)
    if (formData.selectedFile) {
      fd.append('userImage', formData.selectedFile);
    }

    // Basic user information
    fd.append('firstName', formData.firstName);
    if (formData.lastName) fd.append('lastName', formData.lastName);
    fd.append('userName', isSuperAdmin ? `${formData.userName}Admin` : formData.userName);
    fd.append('password', formData.password);
    fd.append('vehicleGroup', formData.selectGroup);

    // Company information (only when not admin)
    if (!isAdmin && formData.company) fd.append('companyName', formData.company);

    // Role/authorities
    if (formData.userRole) fd.append('authorities', formData.userRole);

    // Professional details
    if (formData.experience) fd.append('experience', formData.experience);
    if (formData.jobType) fd.append('jobType', formData.jobType);
    if (formData.dob) fd.append('dob', formData.dob);
    if (formData.dateOfJoining) fd.append('dateOfJoining', formData.dateOfJoining);
    if (formData.licenseNo) fd.append('licenceNo', formData.licenseNo);
    if (formData.employerId) fd.append('employeeId', formData.employerId);

    // Address fields with dot notation
    if (formData.fullAddress) fd.append('address.street', formData.fullAddress);
    if (formData.city) fd.append('address.city', formData.city);
    if (formData.state) fd.append('address.state', formData.state);
    if (formData.country) fd.append('address.country', formData.country);
    if (formData.timeZones) fd.append('address.timeZone', formData.timeZones);

    // Contact information
    if (formData.phone) fd.append('phone', formData.phone);
    if (formData.email) fd.append('email', formData.email);

    // System fields
    if (id !== null && id !== undefined) {
      fd.append('loginUserId', String(id));
    }

    // Debug logging (without exposing sensitive data)
    console.log('Submitting multipart FormData with fields:', {
      hasUserImage: !!formData.selectedFile,
      userImageName: formData.selectedFile?.name,
      firstName: formData.firstName,
      lastName: formData.lastName,
      userName: formData.userName,
      selectGroup: formData.selectGroup,
      companyName: !isAdmin ? formData.company : null,
      authorities: formData.userRole,
      professionalFields: {
        experience: formData.experience,
        jobType: formData.jobType,
        dob: formData.dob,
        dateOfJoining: formData.dateOfJoining,
        licenceNo: formData.licenseNo,
        employeeId: formData.employerId,
      },
      addressFields: {
        street: formData.fullAddress,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        timeZone: formData.timeZones,
      },
      contactFields: {
        email: formData.email,
        phone: formData.phone,
      },
      loginUserId: id,
      isAdmin,
    });

    try {
      // Submit the FormData using the useAxios hook
      // The Content-Type header will be automatically set by the browser with proper boundary
      await UserAddRequestData(fd);

      // Success and error handling is done in the useEffect hooks
      // Do not add toast messages here to avoid duplication
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Extract error message from different possible error formats
      let errorMessage = 'Failed to create user. Please try again.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (UserAddError) {
        errorMessage = UserAddError;
      }

      // Show error toast only if not already shown
      if (!hasShownError) {
        showToast(errorMessage, 'error');
        setHasShownError(true);
      }
    }
  };

  // Monitor API response for errors - only show errors, not success messages
  useEffect(() => {
    if (UserAddError && !hasShownError) {
      console.error('User Add Error:', UserAddError);
      showToast(UserAddError, 'error');
      setHasShownError(true);
    }
  }, [UserAddError, showToast, hasShownError]);

  // Monitor API response data for error status codes - only show errors
  useEffect(() => {
    if (UserAddData && UserAddData.statusCode && UserAddData.statusCode >= 400 && !hasShownError) {
      console.error('User Add Error from response:', UserAddData);
      const errorMessage = UserAddData.message || 'Failed to create user';
      showToast(errorMessage, 'error');
      setHasShownError(true);
    }
  }, [UserAddData, showToast, hasShownError]);

  useEffect(() => {
    // Only handle success case when there's no error status and we have data
    if (UserAddData && !UserAddErrorStatus && !hasShownError) {
      console.log('=== SUCCESS HANDLING ===');
      console.log('UserAddData:', UserAddData);

      // Check for actual error status (400+ indicates error, 200-299 indicates success)
      if (UserAddData.status && UserAddData.status >= 400) {
        // Handle error responses with status codes in the response body
        const errorMessage = UserAddData.message || 'Server error occurred';
        showToast(errorMessage, 'error');
        setHasShownError(true);
      } else if (UserAddData.status && UserAddData.status >= 200 && UserAddData.status < 300) {
        // Handle success responses (200-299 status codes)
        const successMessage = UserAddData.message || 'User created successfully!';
        showToast(successMessage, 'success');
        setHasShownError(true); // Mark as shown to prevent duplicate toasts

        // Reset the form on successful creation
        setFormData({
          company: '',
          timeZones: '',
          userRole: isSuperAdmin ? 'ROLE_ADMIN' : 'ROLE_FLEET_MANAGER',
          userName: '',
          password: '',
          firstName: '',
          lastName: '',
          dob: '',
          experience: '',
          employerId: '',
          jobType: '',
          dateOfJoining: '',
          licenseNo: '',
          email: '',
          phone: '',
          city: '',
          state: '',
          country: '',
          fullAddress: '',
          selectedFile: null,
          selectVehicle: '',
          selectDate: '',
          selectTime: '',
          previewUrl: '',
          selectGroup: '',
        });

        // Reset errors
        setErrors({});

        // Reset user name validation states
        setUserNameExists(false);
        setCheckingUserName(false);

        // Clear any existing timeout
        if (userNameCheckTimeout) {
          clearTimeout(userNameCheckTimeout);
          setUserNameCheckTimeout(null);
        }
      }
    }
  }, [UserAddData, UserAddErrorStatus, hasShownError, showToast, isSuperAdmin, userNameCheckTimeout]);

  // Ensure all mandatory fields (including Company, City, State) are included in overall form validity
  const isFormValid =
    Object.keys(errors).length === 0 &&
    (isAdmin || isFleetManager || !!formData.company) && // company required only when current user is NOT ROLE_ADMIN and NOT ROLE_FLEET_MANAGER
    !!formData.userRole &&
    !!formData.userName &&
    !userNameExists &&
    !checkingUserName &&
    !!formData.password &&
    !!formData.firstName &&
    !!formData.country &&
    !!formData.timeZones &&
    !!formData.state &&
    !!formData.city &&
    (formData.userRole !== 'ROLE_LOCATION_MANAGER' || !!formData.selectGroup); // selectGroup required only for ROLE_LOCATION_MANAGER

  // Render Component
  return (
    <div className="flex flex-col items-center w-full min-h-screen">
      <div className="w-full">
        {/* Back Button */}
        <div className="flex justify-start items-center mb-6">
          <Button
            className="font-medium flex items-center"
            sx={(theme: any) => ({
              color: theme.palette.primary.main,
              textTransform: 'none',
              padding: 0,
              fontSize: '16px',
            })}
            onClick={() => window.history.back()}
          >
            <img src={ArrowBackIcon} className="mr-2" width={24} height={24} alt="Back" />
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px' }}>
            CREATE USER INFORMATION
          </Typography>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Basic Info
            </Typography>
            <Grid container spacing={3}>
              {/* Company Dropdown: Hidden when current user has ROLE_ADMIN or ROLE_FLEET_MANAGER */}
              {!isAdmin && !isFleetManager && (
                <Grid item xs={12} md={4}>
                  <CustomDropDownInput
                    label="Company"
                    name="company"
                    options={companyOptions}
                    value={formData.company}
                    onChange={handleDropdownChange('company')}
                    placeholder="Select Company"
                    isMandatory
                    status={errors.company ? 'error' : undefined}
                    showDropdownArrow
                    isDisabled={companyOptions.length === 0}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="Select User Role"
                  name="userRole"
                  options={roleOptions}
                  value={formData.userRole}
                  onChange={handleDropdownChange('userRole')}
                  placeholder="Select userRole"
                  isMandatory
                  status={errors.userRole ? 'error' : undefined}
                  showDropdownArrow
                  isDisabled={roleOptions.length === 0}
                />
                {errors.userRole && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#f44336',
                      fontSize: '12px',
                      mt: 0.5,
                      display: 'block',
                    }}
                  >
                    {errors.userRole}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <div className="relative">
                  <InputField
                    label="User Name"
                    name="userName"
                    value={formData.userName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\s/g, '');

                      setFormData((prev) => ({
                        ...prev,
                        userName: value,
                      }));
                    }}
                    // onBlur={() => {
                    //   if (isSuperAdmin && formData.userName && !formData.userName.endsWith('Admin')) {
                    //     setFormData((prev) => ({
                    //       ...prev,
                    //       userName: `${prev.userName}Admin`,
                    //     }));
                    //   }
                    // }}
                    isMandatory
                    status={
                      errors.userName || userNameExists
                        ? 'error'
                        : !checkingUserName &&
                          formData.userName &&
                          formData.userName.trim().length >= 2 &&
                          !userNameExists
                          ? 'success'
                          : undefined
                    }
                    errorMessage={
                      errors.userName ||
                      (userNameExists ? 'User name already exists. Please choose a different name.' : '')
                    }
                    placeholder="User Name"
                  />
                  {/* Custom status indicators */}
                  {checkingUserName && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#1976d2',
                        fontSize: '12px',
                        mt: 0.5,
                        display: 'block',
                        fontStyle: 'italic',
                      }}
                    >
                      🔍 Checking availability...
                    </Typography>
                  )}
                  {!checkingUserName &&
                    !userNameExists &&
                    formData.userName &&
                    formData.userName.trim().length >= 2 &&
                    !errors.userName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#4caf50',
                          fontSize: '12px',
                          mt: 0.5,
                          display: 'block',
                          fontWeight: 500,
                        }}
                      >
                        ✓ User name is available
                      </Typography>
                    )}
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  isMandatory
                  status={errors.password ? 'error' : undefined}
                  errorMessage={errors.password}
                  placeholder="Password"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="First Name..."
                  isMandatory
                  status={errors.firstName ? 'error' : undefined}
                  errorMessage={errors.firstName}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Last Name..."
                  status={errors.lastName ? 'error' : undefined}
                  errorMessage={errors.lastName}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <DateField
                  label="DOB"
                  name="dob"
                  value={formData.dob}
                  onChange={(value) => setFormData((prev) => ({ ...prev, dob: value || '' }))}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="Experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Total Experience"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="Employee ID"
                  name="employerId"
                  value={formData.employerId}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Employee Id"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="Job Type"
                  name="jobType"
                  options={[
                    { value: 'Full Time', label: 'Full Time' },
                    { value: 'Part Time', label: 'Part Time' },
                  ]}
                  value={formData.jobType}
                  onChange={handleDropdownChange('jobType')}
                  placeholder="Select Job Type"
                  showDropdownArrow
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <DateField
                  label="Date of Joining"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={(value) => setFormData((prev) => ({ ...prev, dateOfJoining: value || '' }))}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InputField
                  label="License No."
                  name="licenseNo"
                  value={formData.licenseNo}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="License No."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <div className="flex flex-col gap-2">
                  <Typography variant="overline" fontWeight={600} color="secondary">
                    Capture Profile (userImage){' '}
                    {formData.selectedFile && <span className="text-green-500">✓</span>}
                  </Typography>

                  {/* Image Preview */}
                  {formData.previewUrl && (
                    <div className="relative mb-2">
                      <Box
                        sx={{
                          width: '100%',
                          height: '120px',
                          border: '2px dashed #e0e0e0',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={formData.previewUrl}
                          alt="Profile preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                        <Button
                          onClick={handleRemoveImage}
                          sx={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: '16px' }} />
                        </Button>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                        {formData.selectedFile?.name}
                      </Typography>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex flex-wrap items-center gap-2">
                    <CustomIconButton
                      icon={<img src={file} alt="upload" width={24} height={24} />}
                      text={formData.previewUrl ? 'Change Image' : 'Upload Image'}
                      background="transparent"
                      outline
                      padding="0px"
                      variant="outlined"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    />

                    <input
                      type="file"
                      name="selectedFile"
                      accept="image/*"
                      id="file-upload"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />

                    {formData.previewUrl && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveImage}
                        startIcon={<Delete />}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Helper Text */}
                  <Typography variant="caption" color="textSecondary">
                    Supported formats: JPG, PNG, GIF • Max size: 5MB
                  </Typography>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                {formData.userRole === 'ROLE_LOCATION_MANAGER' && (
                  <CustomDropDownInput
                    label="Select Depot"
                    name="selectGroup"
                    options={groupOptions}
                    value={formData.selectGroup}
                    onChange={handleDropdownChange('selectGroup')}
                    placeholder="Select Depot"
                    showDropdownArrow
                    status={errors.selectGroup ? 'error' : undefined}
                    isMandatory
                  />
                )}
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Contacts Info */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Contacts Info
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Email..."
                  status={errors.email ? 'error' : undefined}
                  errorMessage={errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="MobileNo..."
                />
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Address Info */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Address Info
            </Typography>
            <Grid container spacing={3}>
              {/* Country */}
              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="Country"
                  name="country"
                  options={countries}
                  value={countries.find((c) => c.label === formData.country)?.value || ''}
                  onChange={handleDropdownChange('country')}
                  placeholder="Select Country"
                  isMandatory
                  status={errors.country ? 'error' : undefined}
                  showDropdownArrow
                />
              </Grid>

              {/* TimeZone */}
              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="Time Zone"
                  name="timeZones"
                  options={
                    timeZonesData?.data
                      ? Array.isArray(timeZonesData.data)
                        ? timeZonesData.data.map((tz: string) => ({ value: tz, label: tz }))
                        : []
                      : []
                  }
                  value={formData.timeZones}
                  onChange={handleDropdownChange('timeZones')}
                  placeholder="Select Time Zone"
                  isMandatory
                  status={errors.timeZones ? 'error' : undefined}
                  isDisabled={!formData.country}
                  showDropdownArrow
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="State"
                  name="state"
                  options={states}
                  value={formData.state}
                  onChange={handleDropdownChange('state')}
                  placeholder="Select State"
                  isMandatory
                  status={errors.state ? 'error' : undefined}
                  isDisabled={!formData.country}
                  showDropdownArrow
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} md={4}>
                <InputField
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  isMandatory
                  placeholder="City..."
                  status={errors.city ? 'error' : undefined}
                  errorMessage={errors.city}
                />
              </Grid>

              {/* Full Address */}
              <Grid item xs={12}>
                <InputField
                  label="Address"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Full Address here..."
                  rows={4}
                  type="textarea"
                  multiline
                />
              </Grid>
            </Grid>
          </div>

          {/* Form Actions */}
          <div className="relative w-full mt-auto">
            <div className="flex flex-col md:flex-row justify-between py-4 border-t">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => window.history.back()}
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={UserAddLoading || !isFormValid}
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '21px',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 1,
                  textTransform: 'none',
                }}
              >
                {UserAddLoading ? 'Creating User...' : 'SUBMIT'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUser;
